import { type InferResponseType, unwrap } from "@nexxt/api-client";
import { etafStatuses } from "@nexxt/contracts/party";
import { partyQueries } from "@nexxt/party-ui";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of the e-TAFs (50-api/party.md §2.7): the list by page, the queue of the ones that wait, one with its
// form, and the one a customer has.

const applications = api.party["etaf-applications"];
const byId = applications[":applicationId"];

type ListPage = InferResponseType<typeof applications.$get, 200>;
export type EtafSummary = ListPage["items"][number];
export type EtafApplication = InferResponseType<typeof byId.$get, 200>;

/** What the list shows, in the URL (`?view=`): the ones that wait for Tomorrow, all of them, or the ones in a status. */
export const etafViews = ["waiting", "all", ...etafStatuses] as const;
export type EtafView = (typeof etafViews)[number];

export interface EtafFilters {
  q?: string | undefined;
  /** Absent: the ones that wait. */
  view?: EtafView | undefined;
}

const PAGE_SIZE = 50;

const keys = {
  all: [...partyQueries.all, "etaf"] as const,
  lists: [...partyQueries.all, "etaf", "list"] as const,
  details: [...partyQueries.all, "etaf", "detail"] as const,
};

const viewQuery = (view: EtafView = "waiting") =>
  view === "all" ? {} : view === "waiting" ? { waiting: "true" as const } : { status: view };

export const etafQueries = {
  ...keys,

  list: (filters: EtafFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.lists, filters],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          applications.$get(
            {
              query: {
                ...(filters.q && { q: filters.q }),
                ...viewQuery(filters.view),
                page: String(pageParam),
                pageSize: String(PAGE_SIZE),
              },
            },
            { init: { signal } },
          ),
        ),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  detail: (applicationId: string) =>
    queryOptions({
      queryKey: [...keys.details, applicationId],
      queryFn: ({ signal }) => unwrap(byId.$get({ param: { applicationId } }, { init: { signal } })),
    }),

  /** The e-TAF a customer has, or null: a customer has one. */
  ofCustomer: (customerOrgId: string) =>
    queryOptions({
      queryKey: [...keys.all, "customer", customerOrgId],
      queryFn: async ({ signal }) =>
        (await unwrap(applications.$get({ query: { customerOrgId, pageSize: "1" } }, { init: { signal } }))).items[0] ??
        null,
    }),
};
